import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

function F({ label, children }) {
  return (
    <div>
      <Label className="text-zinc-200 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function KYCSection() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ aadhaar_last4: "", phone: "", otp: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/verification/kyc/status").then(r => setStatus(r.data.kyc)).catch(() => {});
  }, []);

  const sendOtp = async () => {
    try {
      const { data } = await api.post("/verification/otp/send");
      setOtpSent(true);
      toast.success(data.hint || "OTP sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "OTP failed"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/verification/kyc/submit", form);
      const { data } = await api.get("/verification/kyc/status");
      setStatus(data.kyc);
      toast.success("KYC submitted — admin will review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KYC failed");
    } finally { setBusy(false); }
  };

  if (status?.phone_otp_verified) {
    return (
      <div data-testid="kyc-done" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5">
        <p className="font-display text-lg font-semibold text-white flex items-center gap-2">
          <Check size={16} className="text-emerald-400"/> KYC on file
        </p>
        <p className="text-xs text-zinc-300 mt-1">Aadhaar ending {status.aadhaar_last4} · Phone {status.phone}</p>
        <p className="text-xs text-zinc-400 mt-1">{status.aadhaar_verified ? "Aadhaar verified by admin ✓" : "Aadhaar pending admin verification…"}</p>
      </div>
    );
  }

  return (
    <div data-testid="kyc-section" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <p className="font-display text-lg font-semibold mb-1 text-white">KYC (identity check)</p>
      <p className="text-xs text-zinc-400 mb-4">Required before your Verified badge goes live. Nothing shared publicly.</p>
      <div className="space-y-3">
        <F label="Last 4 digits of your Aadhaar">
          <Input data-testid="kyc-aadhaar" maxLength={4} inputMode="numeric" value={form.aadhaar_last4} onChange={(e) => setForm({ ...form, aadhaar_last4: e.target.value.replace(/\D/g,'') })} placeholder="1234" className="bg-zinc-900 border-zinc-700"/>
        </F>
        <F label="Mobile number">
          <div className="flex gap-2">
            <Input data-testid="kyc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="flex-1 bg-zinc-900 border-zinc-700"/>
            <Button data-testid="kyc-send-otp" type="button" onClick={sendOtp} disabled={!form.phone} className="rounded-full h-10 px-4 bg-zinc-900 border border-zinc-700 hover:border-[#DC2626]/50 text-sm">Send OTP</Button>
          </div>
        </F>
        {otpSent && (
          <F label="OTP (use 123456 in demo)">
            <Input data-testid="kyc-otp" maxLength={6} inputMode="numeric" value={form.otp} onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g,'') })} placeholder="123456" className="bg-zinc-900 border-zinc-700"/>
          </F>
        )}
        <Button data-testid="kyc-submit" onClick={submit} disabled={busy || !form.aadhaar_last4 || !form.phone || !form.otp} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14}/> Submitting…</> : "Submit KYC"}
        </Button>
        <p className="text-[11px] text-zinc-400">OTP delivery is <span className="text-[#DC2626]">MOCKED</span> for demo. Use <span className="text-white">123456</span> to proceed.</p>
      </div>
    </div>
  );
}
